/**
 * 일정 유형(카테고리) — 색상·라벨·정렬의 단일 진실 원천(SSOT).
 *
 * <p>값(키)과 한글 라벨의 원본은 백엔드 {@code work_calendar/enums/WorkCalendarCategory.java} 이고,
 * 여기서는 <b>색상과 화면 표시 순서</b>를 담당한다. 유형을 추가할 때는 <b>양쪽을 함께</b> 고쳐야 한다.
 * 서버가 여기 없는 값을 내려주면 '기타' 색으로 떨어지되 라벨은 서버가 준 categoryLabel 을 그대로 쓴다.
 *
 * <p>휴가(LEAVE)는 work_calendar 가 아니라 결재(휴가) 쪽에서 합성돼 오는 가상 유형이라 enum 에 없다.
 * 화면에서는 똑같이 하나의 체크박스로 켜고 끌 수 있어야 해서 여기에 함께 정의한다.
 */

/** 색상 규약: bg=칩/막대 배경, bar=좌측 강조선·점, text=글자 */
export const CATEGORY_META = {
    PLANNING_EVENT:    { label: "기획/행사",    bg: "#fee2e2", bar: "#dc2626", text: "#7f1d1d" },
    SYSTEM_TEAM_EVENT: { label: "시스템팀 행사", bg: "#e0f2fe", bar: "#0284c7", text: "#075985" },
    MEETING:           { label: "회의",         bg: "#dbeafe", bar: "#2563eb", text: "#1e3a8a" },
    CLIENT_MEETING:    { label: "미팅",         bg: "#ede9fe", bar: "#7c3aed", text: "#4c1d95" },
    PROPOSAL:          { label: "제안·발표",    bg: "#ffedd5", bar: "#ea580c", text: "#7c2d12" },
    TRAINING:          { label: "교육/워크숍",  bg: "#dcfce7", bar: "#16a34a", text: "#14532d" },
    BUSINESS_TRIP:     { label: "출장",         bg: "#ccfbf1", bar: "#0d9488", text: "#134e4a" },
    /* 대표님 일정 — 앰버. 다른 유형색(빨강·하늘·파랑·보라·주황·초록·청록·회색)과 휴가(노랑) 중
       어느 것과도 인접하지 않아 한눈에 갈린다. CalendarView 의 대표님 배지색과 같은 계열이다. */
    CEO_SCHEDULE:      { label: "대표님 일정",  bg: "#fef3c7", bar: "#b45309", text: "#78350f" },
    ETC:               { label: "기타",         bg: "#f1f5f9", bar: "#64748b", text: "#334155" },
    /** 가상 유형 — 결재(휴가)에서 합성된 일정. 서버 category 값이 아니라 ev.isLeave 로 판정한다. */
    LEAVE:             { label: "휴가",         bg: "#fef9c3", bar: "#ca8a04", text: "#713f12" },
};

/** 필터 체크박스·범례에 뿌릴 순서 — 자주 쓰는 것부터. */
export const CATEGORY_ORDER = [
    "PLANNING_EVENT",
    "SYSTEM_TEAM_EVENT",
    "MEETING",
    "CLIENT_MEETING",
    "PROPOSAL",
    "TRAINING",
    "BUSINESS_TRIP",
    "CEO_SCHEDULE",
    "ETC",
    "LEAVE",
];

/**
 * 유형만으로 '대표님 일정' 이 되는 값.
 *
 * <p>대표님 판정은 두 갈래다 — <b>대표이사가 등록자·참여자</b>이거나, <b>유형이 이것</b>이거나.
 * 앞의 갈래는 사람 목록을 봐야 알 수 있어 CalendarView 가 계산하고, 뒤의 갈래는 값 하나로
 * 끝나므로 여기 상수로 둔다. 두 곳이 같은 이름을 보게 해서 한쪽만 고치는 일을 막는다.
 */
export const CEO_CATEGORY = "CEO_SCHEDULE";

/** 일정 등록/수정 폼의 선택지 — 휴가는 결재로만 생기므로 제외한다. */
export const CATEGORY_OPTIONS = CATEGORY_ORDER
    .filter(k => k !== "LEAVE")
    .map(k => ({ value: k, label: CATEGORY_META[k].label }));

/** 기본 선택 유형 — 폼에서 아무것도 안 고르면 '기타'. 서버 기본값과 같아야 한다. */
export const DEFAULT_CATEGORY = "ETC";

/**
 * 일정 → 유형 키. 휴가는 서버 category 가 없으므로 가상 키 LEAVE 로 본다.
 * 필터 on/off 와 색상이 <b>같은 함수</b>를 쓰게 해서 "필터로 껐는데 색은 다른 유형" 같은 어긋남을 막는다.
 */
export function categoryKeyOf(ev) {
    if (!ev) return DEFAULT_CATEGORY;
    if (ev.isLeave) return "LEAVE";
    return CATEGORY_META[ev.category] ? ev.category : DEFAULT_CATEGORY;
}

/** 유형 메타(색·라벨). 모르는 값이면 '기타' 메타. */
export function categoryMeta(key) {
    return CATEGORY_META[key] || CATEGORY_META[DEFAULT_CATEGORY];
}

/** 화면에 쓸 유형 라벨 — 서버가 준 categoryLabel 을 우선(라벨의 진실 원천은 서버). */
export function categoryLabelOf(ev) {
    if (!ev) return CATEGORY_META[DEFAULT_CATEGORY].label;
    if (ev.isLeave) return CATEGORY_META.LEAVE.label;
    return ev.categoryLabel || categoryMeta(categoryKeyOf(ev)).label;
}
